import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  ArrowRight,
  CheckCircle,
  Users,
  BarChart3,
  Award,
  BookOpen,
  Monitor,
  Shield,
  Zap,
  Globe,
  Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export const PlataformaEAD = ({ onDemoClick }) => {
  const features = [
    {
      icon: Globe,
      title: 'Domínio Personalizado',
      description: 'Sua universidade corporativa com URL própria e identidade visual da empresa.'
    },
    {
      icon: BookOpen,
      title: 'Trilhas de Aprendizagem',
      description: 'Organize cursos em trilhas sequenciais para desenvolvimento estruturado.'
    },
    {
      icon: Users,
      title: 'Gestão de Turmas',
      description: 'Crie turmas, defina prazos e acompanhe o progresso de grupos específicos.'
    },
    {
      icon: Award,
      title: 'Certificados Automáticos',
      description: 'Emissão automática de certificados personalizados ao concluir cursos.'
    },
    {
      icon: BarChart3,
      title: 'Dashboards e Relatórios',
      description: 'Métricas completas de engajamento, conclusão e desempenho.'
    },
    {
      icon: Zap,
      title: 'Gamificação',
      description: 'Rankings, pontos e badges para aumentar o engajamento dos colaboradores.'
    },
    {
      icon: Monitor,
      title: 'Biblioteca de Conteúdo',
      description: 'Diversos formatos: vídeos, PDFs, SCORM, H5P, podcasts e mais.'
    },
    {
      icon: Smartphone,
      title: 'Acesso Mobile',
      description: 'Plataforma responsiva e aplicativo opcional para aprender em qualquer lugar.'
    },
    {
      icon: Shield,
      title: 'Segurança e Privacidade',
      description: 'Dados protegidos, backup automático e conformidade com LGPD.'
    },
  ];

  const differentials = [
    {
      title: 'Personalização Total',
      description: 'Cada plataforma é única. Configuramos cores, logos, domínio e funcionalidades de acordo com suas necessidades específicas.',
    },
    {
      title: 'Suporte Especializado',
      description: 'Equipe dedicada para ajudar na implantação, configuração e treinamento dos administradores.',
    },
    {
      title: 'Integrações',
      description: 'Conectamos com sistemas de RH, SSO, e outras ferramentas que sua empresa já utiliza.',
    },
    {
      title: 'Escalabilidade',
      description: 'Infraestrutura robusta para suportar desde 50 até milhares de colaboradores simultaneamente.',
    },
  ];

  const faqs = [
    {
      question: 'Qual a diferença entre a plataforma CSP e outras do mercado?',
      answer: 'Nossa plataforma é personalizada para cada cliente, não é uma solução genérica. Você tem domínio próprio, identidade visual da sua empresa e funcionalidades configuradas para suas necessidades. Além disso, oferecemos suporte humano especializado, não apenas bots.'
    },
    {
      question: 'Preciso ter conteúdo pronto para começar?',
      answer: 'Não necessariamente. Podemos começar com cursos que você já possui e também oferecemos serviço de produção de conteúdo. Muitos clientes começam com poucos cursos e vão expandindo gradualmente.'
    },
    {
      question: 'A plataforma funciona em celular?',
      answer: 'Sim! A plataforma é totalmente responsiva e funciona em qualquer dispositivo. Também oferecemos aplicativo mobile como opcional para empresas que preferem essa experiência.'
    },
    {
      question: 'Vocês fazem integração com nosso sistema de RH?',
      answer: 'Sim, fazemos integrações com os principais sistemas de RH do mercado, incluindo SSO (Single Sign-On) para login unificado. Cada integração é avaliada individualmente pela nossa equipe técnica.'
    },
    {
      question: 'Existe limite de usuários ou cursos?',
      answer: 'Os planos são baseados na quantidade de usuários ativos. Não há limite para quantidade de cursos, vídeos ou materiais dentro da plataforma.'
    },
  ];

  return (
    <div data-testid="plataforma-ead-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur text-sm font-medium mb-6">
                <GraduationCap className="h-4 w-4" />
                Plataforma EAD
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Sua Faculdade{' '}
                <span className="text-orange-400">Corporativa</span>{' '}
                Personalizada
              </h1>
              <p className="text-lg text-blue-100 mb-8 max-w-xl">
                Plataforma completa de ensino a distância com sua marca, domínio próprio 
                e todas as ferramentas para gerenciar a educação corporativa da sua empresa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={onDemoClick}
                  data-testid="ead-hero-demo-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold"
                >
                  Solicitar Demonstração
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=500&fit=crop"
                  alt="Plataforma EAD CSP"
                  className="rounded-xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Recursos <span className="text-blue-600">Completos</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tudo que você precisa para criar, gerenciar e acompanhar os treinamentos da sua empresa.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="h-full p-6 border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all group">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                      <feature.icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentials */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Por que escolher a{' '}
                <span className="text-orange-500">CSP</span>?
              </h2>
              <div className="space-y-6">
                {differentials.map((diff, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mt-1">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{diff.title}</h3>
                      <p className="text-slate-600">{diff.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=600&fit=crop"
                alt="Equipe CSP"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-400 text-sm font-medium mb-6">
                <Shield className="h-4 w-4" />
                Segurança e Escalabilidade
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Infraestrutura <span className="text-orange-400">robusta</span> e segura
              </h2>
              <p className="text-slate-400 mb-8">
                Sua plataforma hospedada em infraestrutura de alta disponibilidade, 
                com backups automáticos e conformidade com as melhores práticas de segurança.
              </p>
              <ul className="space-y-4">
                {[
                  'Servidores de alta disponibilidade',
                  'Backup diário automático',
                  'Criptografia de dados sensíveis',
                  'Conformidade com LGPD',
                  'Suporte para milhares de acessos simultâneos',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              <Card className="bg-slate-800 border-slate-700 p-6 text-center">
                <CardContent className="p-0">
                  <p className="text-4xl font-bold text-white mb-2">99.9%</p>
                  <p className="text-slate-400 text-sm">Uptime garantido</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700 p-6 text-center">
                <CardContent className="p-0">
                  <p className="text-4xl font-bold text-white mb-2">SSL</p>
                  <p className="text-slate-400 text-sm">Conexão segura</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700 p-6 text-center">
                <CardContent className="p-0">
                  <p className="text-4xl font-bold text-white mb-2">LGPD</p>
                  <p className="text-slate-400 text-sm">Conformidade</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700 p-6 text-center">
                <CardContent className="p-0">
                  <p className="text-4xl font-bold text-white mb-2">24/7</p>
                  <p className="text-slate-400 text-sm">Monitoramento</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Perguntas <span className="text-blue-600">Frequentes</span>
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl border-0 px-6"
                data-testid={`ead-faq-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Comece sua Universidade Corporativa hoje
            </h2>
            <p className="text-lg text-blue-100 mb-8">
              Solicite uma demonstração e veja como a plataforma pode transformar 
              a educação da sua empresa.
            </p>
            <Button
              onClick={onDemoClick}
              data-testid="ead-cta-demo-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold"
            >
              Solicitar Demonstração Gratuita
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
